import { makeAutoObservable, runInAction, reaction } from 'mobx';
import { VideoEditor } from './VideoEditor';
import { Timeline } from './models/Timeline';
import { Track } from './models/Track';
import { Element } from './models/Element';
import { Renderer } from '../renderer/Renderer';
import { RootStore } from '../stores-v2/RootStore';
import { TimelineMode } from './commands/TimelineMode';
import { EditorChangeType } from './types';
import { TranscriptElement } from '@src/types.ts/transcript';

/**
 * EditorStore is a MobX wrapper around VideoEditor for reactivity
 */
export class EditorStore {
  // Core state
  editor: VideoEditor;
  timeline: Timeline;
  isReady: boolean;
  activeTranscript: any[] = [];
  private _inferredFullTranscript: any[] = [];
  headlessEnabled: boolean = false;

  // Selection state
  selectedElementIds: string[] = [];

  // Root store reference
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.editor = new VideoEditor();
    this.timeline = this.editor.timeline;

    this.isReady = runInAction(() => false);

    // Listen for editor state changes to update the local timeline
    this.editor.onStateChange = (timeline) => {
      runInAction(() => {
        this.timeline = timeline;
      });
    };

    makeAutoObservable(this, {
      // rootStore: false,
      editor: false,
    } as any);

    // Set up reactions
    this.initReactions();
  }

  /**
   * Initializes the editor with a renderer instance
   */
  initializeEditor(
    htmlElement: HTMLDivElement,
    mode: 'player' | 'interactive',
  ): Renderer {
    const renderer = new Renderer(
      htmlElement,
      mode,
      'public-juuabbc8amz25dcfhribv3ss', // This should eventually come from config
    );

    renderer.onReady = () => {
      runInAction(() => {
        this.isReady = true;
      });

      // Connect the editor to the renderer
      this.editor.connectRenderer(renderer);

      // Register MobX reactivity observer
      this.setupEditorObservers();

      // Initialize the observer pattern
      this.editor.initializeBuiltInObservers();
    };

    // Return the renderer so it can be shared with other components
    return renderer;
  }

  /**
   * Set up MobX reactions
   */
  private initReactions(): void {
    // Example: sync with transcription store when available
    reaction(
      () => this.rootStore.transcriptionStore.transcriptionElements,
      (transcriptionElements) => {
        // This would be implemented in Phase 4 to sync transcript changes
        // with the editor timeline
      },
    );

    // Initialize activeTranscript when editor loads a transcript
    reaction(
      () => this.editor.getActiveTranscript(),
      (activeTranscript) => {
        if (activeTranscript) {
          runInAction(() => {
            this.activeTranscript = [...activeTranscript];

            // WIP - Inferred
            // const computed = this.editor.getInferredFullTranscript();
            // this._inferredFullTranscript = computed;
            // console.log(`Reaction updated inferred transcript, length: ${computed?.length || 0}`);
          });
        }
      },
    );
  }

  /**
   * Setup observers to ensure MobX reactivity for editor changes
   */
  private setupEditorObservers(): void {
    const mobxReactivityObserver = {
      onEditorChange: (
        editor: VideoEditor,
        changeType: EditorChangeType,
        payload: Record<string, any>,
      ) => {
        if (
          changeType === EditorChangeType.TIMELINE_CHANGED ||
          changeType === EditorChangeType.ELEMENT_ADDED ||
          changeType === EditorChangeType.ELEMENT_REMOVED ||
          changeType === EditorChangeType.ELEMENT_UPDATED
        ) {
          this.refreshTimeline();
        }
        if (changeType === EditorChangeType.ACTIVE_TRANSCRIPT_CHANGED) {
          this.refreshTranscript(payload?.activeTranscript);
        }
      },
    };

    this.editor.registerObserver(mobxReactivityObserver);
  }

  /**
   * Force refresh the timeline reference to trigger MobX reactivity
   * This is a critical method for ensuring the UI updates when the timeline changes
   */
  refreshTimeline(): void {
    runInAction(() => {
      // Update our reference to match the editor's timeline
      this.timeline = this.editor.timeline;
    });
  }

  /**
   * Update local transcript state to trigger MobX reactivity
   */
  refreshTranscript(payload: any): void {
    // Handle both direct array and payload object formats
    const activeTranscript = Array.isArray(payload)
      ? payload
      : payload?.activeTranscript;
    const inferredTranscript = payload?.inferredTranscript;

    if (activeTranscript || inferredTranscript) {
      runInAction(() => {
        // If active transcript is provided, update it
        if (activeTranscript) {
          this.activeTranscript = [...activeTranscript];
        }

        // if (inferredTranscript) {
        //   console.log(
        //     `Payload included inferred transcript with ${inferredTranscript.length} elements`,
        //   );
        //   this._inferredFullTranscript = [...inferredTranscript];
        // }
      });
    }
  }

  /**
   * Creates a new track
   */
  addTrack(type: 'video' | 'audio' | 'text' | 'image' = 'video'): Track {
    return this.editor.commandCreateTrack(type);
  }

  /**
   * Adds an element to a track
   */
  addElementToTrack(trackId: string, element: Element): boolean {
    return this.editor.commandAddElementToTrack(trackId, element);
  }

  /**
   * Creates a new element and adds it to a track
   */
  createAndAddElement(
    trackId: string,
    type: string,
    props: Record<string, any> = {},
  ): Element | null {
    return this.editor.commandCreateAndAddElement(trackId, type, props);
  }

  /**
   * Updates an element's property
   */
  updateElementProperty(elementId: string, key: string, value: any): boolean {
    return this.editor.commandUpdateElementProperty(elementId, key, value);
  }

  /**
   * Updates multiple element properties at once
   */
  updateElement(elementId: string, props: Partial<Element>): boolean {
    return this.editor.commandUpdateElement(elementId, props);
  }

  /**
   * Removes an element from the timeline
   * @param elementId Element ID string or Element object
   * @returns True if the element was successfully removed
   */
  removeElement(elementId: string): boolean {
    return this.editor.commandRemoveElement(elementId);
  }

  /**
   * Selects a single element (clearing previous selection)
   */
  selectElement(elementId: string): void {
    runInAction(() => {
      this.selectedElementIds = [elementId];

      // Notify observers about the selection change
      if (this.editor) {
        this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
          selectedElementIds: this.selectedElementIds,
          selectedElements: this.selectedElements,
        });
      }

      // Make sure this triggers reactivity
      setTimeout(() => this.refreshTimeline(), 0);
    });
  }

  /**
   * Adds an element to the current selection
   */
  addToSelection(elementId: string): void {
    runInAction(() => {
      if (!this.selectedElementIds.includes(elementId)) {
        this.selectedElementIds.push(elementId);

        // Notify observers about the selection change
        if (this.editor) {
          this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
            selectedElementIds: this.selectedElementIds,
            selectedElements: this.selectedElements,
          });
        }
      }
    });
  }

  /**
   * Alias for addToSelection for API consistency
   */
  addElementToSelection(elementId: string): void {
    this.addToSelection(elementId);
  }

  /**
   * Removes an element from the current selection
   */
  removeFromSelection(elementId: string): void {
    runInAction(() => {
      this.selectedElementIds = this.selectedElementIds.filter(
        (id) => id !== elementId,
      );

      // Notification happens in the deselectElement method to avoid duplicate events
    });
  }

  /**
   * Alias for removeFromSelection for API consistency
   */
  deselectElement(elementId: string): void {
    this.removeFromSelection(elementId);

    // Notify observers about the selection change
    if (this.editor) {
      this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
        selectedElementIds: this.selectedElementIds,
        selectedElements: this.selectedElements,
      });
    }
  }

  /**
   * Clears the current selection
   */
  clearSelection(): void {
    runInAction(() => {
      this.selectedElementIds = [];

      // Notify observers about the selection change
      if (this.editor) {
        this.editor.notifyObservers(EditorChangeType.SELECTION_CHANGED, {
          selectedElementIds: this.selectedElementIds,
          selectedElements: this.selectedElements,
        });
      }
    });
  }

  /**
   * Gets the currently selected elements
   */
  get selectedElements(): Element[] {
    // First log how many elements should match
    const ids = this.selectedElementIds;

    // Then return elements that match the selected IDs
    const elements = this.timeline.allElements.filter((element) =>
      ids.includes(element.id),
    );

    // Log how many were found
    if (ids.length > 0 && elements.length === 0) {
      console.warn('No matching elements found for selected IDs');
    }

    return elements;
  }

  /**
   * Gets a track by ID
   */
  getTrackById(trackId: string): Track | undefined {
    return this.editor.getTrackById(trackId);
  }

  /**
   * Gets an element by ID
   */
  getElementById(elementId: string): Element | undefined {
    return this.editor.getElementById(elementId);
  }

  /**
   * Gets the current transcription elements
   */
  get transcriptionElements() {
    return this.rootStore.transcriptionStore.transcriptionElements;
  }

  /**
   * Gets the current active transcript from the editor
   */
  get currentActiveTranscript() {
    return this.activeTranscript;
  }

  // /**
  //  * Gets the current inferred transcript from the editor
  //  * Computes it on-demand if not already cached
  //  */
  // get inferredFullTranscript() {
  //   // If we already have a cached version, return it
  //   if (
  //     this._inferredFullTranscript &&
  //     this._inferredFullTranscript.length > 0
  //   ) {
  //     return this._inferredFullTranscript;
  //   }

  //   // Otherwise, compute it directly from the editor
  //   const computed = this.editor.getInferredFullTranscript();

  //   // Cache it for future use
  //   runInAction(() => {
  //     this._inferredFullTranscript = computed;
  //   });

  //   return computed;
  // }

  // /**
  //  * Sets the inferred transcript
  //  */
  // set inferredFullTranscript(value: any[]) {
  //   this._inferredFullTranscript = value;
  // }

  /**
   * Load a project from JSON
   */
  loadProject(
    json: Record<string, any>,
    transcription: TranscriptElement[] = [],
  ): void {
    this.editor.loadProject(json);
    this.editor.loadTranscript(transcription);
  }

  /**
   * Gets the current timeline mode
   */
  get timelineMode(): TimelineMode {
    return this.editor.getTimelineMode();
  }

  /**
   * Sets the timeline mode
   */
  setTimelineMode(mode: TimelineMode): void {
    this.editor.setTimelineMode(mode);
  }

  /**
   * Undoes the last operation
   */
  undo(): boolean {
    return this.editor.undo();
  }

  /**
   * Redoes a previously undone operation
   */
  redo(): boolean {
    return this.editor.redo();
  }

  /**
   * Disconnects from the current renderer
   * Should be called when the renderer is being disposed or changed
   */
  disconnectRenderer(): void {
    // Since we don't have a direct disconnectRenderer method, we'll just
    // reset things to a clean state by refreshing the internal timeline
    this.refreshTimeline();
  }

  /**
   * Moves an element to a new time position
   * @param elementId Element ID string or Element object
   * @param newStartTime New start time in seconds
   * @param targetTrack Optional target track to move to
   */
  moveElement(
    elementId: string,
    newStartTime: number,
    targetTrack?: Track,
  ): boolean {
    // Call the method with the direct object reference
    return this.editor.commandShiftElement(elementId, newStartTime);
  }

  /**
   * Trims the start of an element
   * @param elementId Element ID string or Element object
   * @param newStartTime New start time in seconds
   */
  trimElementStart(elementId: string, newStartTime: number): boolean {
    return this.editor.commandTrimElementStart(elementId, newStartTime);
  }

  /**
   * Trims the end of an element
   * @param elementId Element ID string or Element object
   * @param newEndTime New end time in seconds
   */
  trimElementEnd(elementId: string, newEndTime: number): boolean {
    return this.editor.commandTrimElementEnd(elementId, newEndTime);
  }

  /**
   * Splits an element at the specified time
   * @param elementId Element ID string
   * @param splitTime Time position where to split the element (in seconds)
   * @returns An array with the two resulting elements, or null if the operation failed
   */
  splitElement(
    elementId: string,
    splitTime: number,
  ): [Element, Element] | null {
    return this.editor.commandSplitElement(elementId, splitTime);
  }

  /**
   * Shifts a track up in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  shiftTrackLayerUp(trackId: string): boolean {
    return this.editor.commandShiftTrackLayerUp(trackId);
  }

  /**
   * Shifts a track down in the layer stack
   * @param trackId The ID of the track to shift
   * @returns true if successful, false otherwise
   */
  shiftTrackLayerDown(trackId: string): boolean {
    return this.editor.commandShiftTrackLayerDown(trackId);
  }
}
